import { useEffect, useRef, useState } from "react";
import { ExpandedState } from "@tanstack/react-table";
import { NestedTaskRow } from "./types";

/**
 * Controlled expanded state for the grouped task table. Groups expand the
 * first time they have tasks, so a manual collapse sticks across refetches.
 * Groups that are no longer rendered (another group-by, a filtered-out
 * status) are dropped, so they don't count as expanded and expand again when
 * they come back.
 */
export const useTaskGroupsExpansion = (groups: readonly NestedTaskRow[]) => {
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const seenGroupIdsRef = useRef(new Set<string>());

    useEffect(() => {
        const seenGroupIds = seenGroupIdsRef.current;
        const currentGroupIds = new Set(groups.map((group) => group.id));
        seenGroupIds.forEach((id) => {
            if (!currentGroupIds.has(id)) {
                seenGroupIds.delete(id);
            }
        });
        const newGroupIds = groups
            .filter(
                (group) =>
                    group.tasks.length > 0 && !seenGroupIds.has(group.id),
            )
            .map((group) => group.id);
        newGroupIds.forEach((id) => seenGroupIds.add(id));

        setExpanded((prev) => {
            const prevEntries = Object.entries(prev === true ? {} : prev);
            const keptEntries = prevEntries.filter(([id]) =>
                currentGroupIds.has(id),
            );
            const isUnchanged =
                newGroupIds.length === 0 &&
                keptEntries.length === prevEntries.length;
            if (isUnchanged) {
                return prev;
            }
            return Object.fromEntries([
                ...keptEntries,
                ...newGroupIds.map((id) => [id, true]),
            ]);
        });
    }, [groups]);

    return { expanded, setExpanded };
};
